import React from 'react';
import { Link } from 'react-router-dom';
import { PPEData } from '../types/ppe';

interface HomePageProps {
  data: PPEData;
}

const HomePage: React.FC<HomePageProps> = ({ data }) => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 text-black">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url(/hero-bg.jpg)' }}
        />
        
        {/* Overlay for peaceful vibe */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/80 to-gray-50"></div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl title-font mb-6">
            PPE Certificate Portal
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Find, understand, and match safety equipment to the right EU standards - all in one place.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link 
              to="/search" 
              className="group px-8 py-4 bg-yellow-500/90 backdrop-blur-sm text-gray-900 font-bold text-lg rounded-full border border-yellow-400/50 shadow-lg hover:shadow-yellow-500/25 transition-all duration-300 transform hover:scale-102 hover:-translate-y-1"
            >
              Search Certificates & Find Matching Products
            </Link>
            <Link 
              to="/categories" 
              className="group px-8 py-4 bg-white/30 backdrop-blur-sm text-gray-900 font-bold text-lg rounded-full border-2 border-white/50 hover:bg-white/40 hover:border-white/70 transition-all duration-300 transform hover:scale-102 hover:-translate-y-1 shadow-md"
            >
              Browse Categories
            </Link>
          </div>
          <div className="mt-8 text-sm opacity-80">
            Powered by Safety Workwear Shop
          </div>
        </div>
      </section>

      {/* PPE Categories */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl title-font text-black mb-4">Certification Categories</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore different types of personal protective equipment and their certification requirements.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.categories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.id}`}
                className="card hover:scale-105 transition-transform duration-200 group"
              >
                <div className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 overflow-hidden rounded">
                    {category.certificates.length > 0 && 
                     category.certificates[0].products && 
                     category.certificates[0].products.length > 0 ? (
                      <img 
                        src={category.certificates[0].products[0].imageUrl} 
                        alt={category.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-gray-600 font-semibold text-2xl">
                        {category.name.charAt(0)}
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-2">{category.name}</h3>
                  <p className="text-gray-600 mb-4">{category.description}</p>
                  <div className="text-gray-500 font-medium group-hover:text-gray-700 transition-colors duration-200">
                    View Certificates →
                  </div>
                </div>
              </Link>
            ))}
          </div>
          
          {/* View All Categories Link */}
          <div className="mt-8 text-center">
            <Link to="/categories" className="text-gray-600 hover:text-gray-900 transition-colors duration-200 font-medium">
              View All Categories & Risk Levels →
            </Link>
          </div>
        </div>
      </section>

      {/* Risk Levels Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl title-font text-black mb-4">Understanding Risk Levels</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              EU PPE regulations categorize personal protective equipment into three risk levels based on the severity of potential hazards. 
              Understanding these levels helps you choose the right protection and ensures compliance with safety standards.
            </p>
          </div>
          
          {/* Risk Level Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {Object.entries(data.riskLevels).map(([level, info]) => (
              <div key={level} className="card text-center border-2 hover:border-blue-500 transition-all duration-300">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-50 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-blue-200">
                  <span className="text-blue-700 font-bold text-2xl">{level}</span>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{info.name}</h3>
                <p className="text-gray-600 mb-4">{info.description}</p>
                <div className="text-sm text-gray-500">
                  {level === 'I' && 'Self-certification'}
                  {level === 'II' && 'Notified body testing'}
                  {level === 'III' && 'Ongoing monitoring'}
                </div>
              </div>
            ))}
          </div>
          
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl title-font mb-4">Ready to Find Your PPE?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Search our comprehensive database of EU PPE certificates and find products that meet your safety requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/search" className="btn-primary">
              Start Searching
            </Link>
            <a 
              href="https://www.safety-workwear-shop.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              Visit Main Shop
            </a>
          </div>
          <div className="mt-8 flex items-center justify-center space-x-3">
            <span className="text-gray-400">Powered by</span>
            <img 
              src="/sws-logo.png" 
              alt="Safety Workwear Shop" 
              className="h-8 w-auto opacity-80"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
